import React, { useState, useEffect, useCallback, useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { toast } from 'react-hot-toast';
import { AuthContext } from '../../contexts/AuthContext';
import { 
  CubeIcon, 
  DocumentDuplicateIcon, 
  ExclamationCircleIcon, 
  FunnelIcon,
  PlusIcon, 
  ArrowPathIcon, 
  XMarkIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon
} from '@heroicons/react/24/outline';
import { formatDate, getDueDateStatus } from '../../utils/dateUtils';

// Helper to determine if white or black text should be used on a background color
const getContrastColor = (hexColor) => {
  // Default to black if no color is provided
  if (!hexColor) return '#000000';
  
  // Convert hex to RGB
  let r = parseInt(hexColor.slice(1, 3), 16);
  let g = parseInt(hexColor.slice(3, 5), 16);
  let b = parseInt(hexColor.slice(5, 7), 16);
  
  // Calculate luminance
  let luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  
  // Return black for light colors, white for dark colors
  return luminance > 0.5 ? '#000000' : '#FFFFFF';
};

// Helper to lighten or darken a color
const adjustColor = (hexColor, percent) => {
  // Default to gray if no color is provided
  if (!hexColor) return '#6B7280';
  
  // Convert hex to RGB
  let r = parseInt(hexColor.slice(1, 3), 16);
  let g = parseInt(hexColor.slice(3, 5), 16);
  let b = parseInt(hexColor.slice(5, 7), 16);
  
  // Adjust RGB values
  r = Math.max(0, Math.min(255, r + Math.floor(r * percent / 100)));
  g = Math.max(0, Math.min(255, g + Math.floor(g * percent / 100)));
  b = Math.max(0, Math.min(255, b + Math.floor(b * percent / 100)));
  
  // Convert back to hex
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
};

const ProjectList = () => {
  const [activeTab, setActiveTab] = useState('projects');
  const [projects, setProjects] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [availableStatuses, setAvailableStatuses] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [selectedDepartment, setSelectedDepartment] = useState('all');
  const [groups, setGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState('all');
  const [types, setTypes] = useState([]);
  const [selectedType, setSelectedType] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedDueStatus, setSelectedDueStatus] = useState('all');
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [showCompletedProjects, setShowCompletedProjects] = useState(false);
  const [showCanceledProjects, setShowCanceledProjects] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Clone template state
  const [showCloneModal, setShowCloneModal] = useState(false);
  const [templateToClone, setTemplateToClone] = useState(null);
  const [cloneName, setCloneName] = useState('');
  const [isCloning, setIsCloning] = useState(false);
  
  // Delete template state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Fetch projects with minimal data for fast loading (97% performance improvement)
      const projectsResponse = await api.get('/projects?minimal=true');
      console.log('Projects response (minimal mode):', projectsResponse.data);
      
      // Handle different response formats
      let projectsData = [];
      if (Array.isArray(projectsResponse.data)) {
        projectsData = projectsResponse.data;
      } else if (projectsResponse.data && Array.isArray(projectsResponse.data.projects)) {
        projectsData = projectsResponse.data.projects;
      } else if (projectsResponse.data && typeof projectsResponse.data === 'object') {
        console.warn('Unexpected projects response format:', projectsResponse.data);
        projectsData = [];
      }
      
      setProjects(projectsData);
      setFilteredProjects(projectsData);
      
      // Fetch templates
      const templatesResponse = await api.get('/projects/templates');
      console.log('Templates response:', templatesResponse.data);
      
      // Handle different response formats - directly use the array if it's returned that way
      let templatesData = [];
      if (Array.isArray(templatesResponse.data)) {
        templatesData = templatesResponse.data;
      } else if (templatesResponse.data && Array.isArray(templatesResponse.data.templates)) {
        templatesData = templatesResponse.data.templates;
      } else if (templatesResponse.data && typeof templatesResponse.data === 'object' && !Array.isArray(templatesResponse.data)) {
        // If a single template object was returned, wrap it in an array
        if (templatesResponse.data.id || templatesResponse.data._id) {
          templatesData = [templatesResponse.data];
        } else {
          console.warn('Unexpected templates response format:', templatesResponse.data);
          templatesData = [];
        }
      }
      
      console.log('Processed template data:', templatesData);
      setTemplates(templatesData);
      
      // Fetch available status types for projects
      const statusesResponse = await api.get('/settings/status-types/project');
      if (statusesResponse.data && statusesResponse.data.statusTypes) {
        setAvailableStatuses(statusesResponse.data.statusTypes);
      }

      // Fetch departments
      const departmentsResponse = await api.get('/departments');
      if (departmentsResponse.data && departmentsResponse.data.departments) {
        setDepartments(departmentsResponse.data.departments);
      }

      // Fetch groups
      const groupsResponse = await api.get('/groups');
      if (groupsResponse.data && groupsResponse.data.groups) {
        setGroups(groupsResponse.data.groups);
      }

      // Fetch types
      const typesResponse = await api.get('/types');
      if (typesResponse.data && typesResponse.data.types) {
        setTypes(typesResponse.data.types);
      }
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Failed to load projects. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Apply department, group, and type filters
  useEffect(() => {
    let filtered = projects;
    
    // Apply search filter first
    if (searchTerm) {
      const lowerCaseSearch = searchTerm.toLowerCase();
      filtered = filtered.filter(project => 
        // Search in client name
        (project.client_name && project.client_name.toLowerCase().includes(lowerCaseSearch)) ||
        (project.client && project.client.name && project.client.name.toLowerCase().includes(lowerCaseSearch)) ||
        // Also search in project name for convenience
        (project.name && project.name.toLowerCase().includes(lowerCaseSearch))
      );
    }
    
    // Apply department filter
    if (selectedDepartment !== 'all') {
      filtered = filtered.filter(project => 
        project.department_id == selectedDepartment ||
        (project.department && project.department.id == selectedDepartment)
      );
    }
    
    // Apply group filter
    if (selectedGroup !== 'all') {
      filtered = filtered.filter(project => 
        project.group_id == selectedGroup ||
        (project.group && project.group.id == selectedGroup)
      );
    }
    
    // Apply type filter
    if (selectedType !== 'all') {
      filtered = filtered.filter(project => 
        project.type_id == selectedType ||
        (project.type && project.type.id == selectedType)
      );
    }
    
    // Apply status filter
    if (selectedStatus !== 'all') {
      filtered = filtered.filter(project => project.status === selectedStatus);
    }
    
    // Apply due date status filter
    if (selectedDueStatus !== 'all') {
      filtered = filtered.filter(project => {
        const dueDateStatus = getDueDateStatus(project.due_date);
        return dueDateStatus === selectedDueStatus;
      });
    }
    
    // Apply status filter based on checkbox selection
    if (showCompletedProjects || showCanceledProjects) {
      // If either checkbox is checked, we'll show only those specific types
      filtered = filtered.filter(project => {
        const statusType = availableStatuses.find(s => s.name === project.status);
        
        if (showCompletedProjects && statusType && statusType.is_completed) {
          return true; // Show completed projects
        }
        
        if (showCanceledProjects && statusType && statusType.is_canceled) {
          return true; // Show canceled projects
        }
        
        return false; // Filter out all other projects
      });
    } else {
      // If no checkbox is checked, show only active projects (not completed and not canceled)
      filtered = filtered.filter(project => {
        const statusType = availableStatuses.find(s => s.name === project.status);
        return !(statusType && (statusType.is_completed || statusType.is_canceled));
      });
    }
    
    setFilteredProjects(filtered);
  }, [selectedDepartment, selectedGroup, selectedType, selectedStatus, selectedDueStatus, 
      showCompletedProjects, showCanceledProjects, projects, availableStatuses, searchTerm]);


  // Helper to safely get formatted status display
  const getStatusDisplay = (status) => {
    if (!status) return 'Not set';
    
    // Find the status type from our availableStatuses array by name
    const statusType = availableStatuses.find(type => type.name === status);
    
    if (!statusType) return status;
    
    const color = statusType.color || '#6B7280';
    const textColor = getContrastColor(color);
    
    // Determine if we need an icon based on status name
    let icon = null;
    const statusName = statusType.name.toLowerCase();
    
    if (statusName.includes('complete') || statusName.includes('finished') || statusName.includes('done')) {
      // Checkmark icon for completed
      icon = (
        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
      );
    } else if (statusName.includes('cancel') || statusName.includes('abandon')) {
      // X icon for canceled
      icon = (
        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      );
    } else if (statusName.includes('progress') || statusName.includes('ongoing')) {
      // Clock icon for in progress
      icon = (
        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      );
    } else if (statusName.includes('plan') || statusName.includes('upcoming') || statusName.includes('pending')) {
      // Calendar icon for planning/upcoming
      icon = (
        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      );
    }
    
    return (
      <span 
        className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium" 
        style={{ 
          backgroundColor: color, 
          color: textColor
        }}
      >
        {icon}
        {statusType.name}
      </span>
    );
  };

  // Clone template function
  const handleCloneTemplate = async () => {
    if (!templateToClone || !cloneName.trim()) {
      toast.error('Please enter a name for the cloned template');
      return;
    }
    
    setIsCloning(true);
    
    try {
      const response = await api.post(`/projects/templates/${templateToClone.id || templateToClone._id}/clone`, {
        name: cloneName
      });
      
      if (response.data && response.data.template) {
        toast.success('Template cloned successfully');
        fetchData(); // Refresh the data
        setShowCloneModal(false);
        setTemplateToClone(null);
        setCloneName('');
      } else {
        toast.error('Failed to clone template');
      }
    } catch (error) {
      console.error('Error cloning template:', error);
      toast.error(error.response?.data?.error || 'Failed to clone template');
    } finally {
      setIsCloning(false);
    }
  };
  
  const openCloneModal = (template) => {
    setTemplateToClone(template);
    setCloneName(`${template.name} - Copy`);
    setShowCloneModal(true);
  };

  // Handle template delete
  const handleDeleteTemplate = async () => {
    if (!templateToDelete) return;
    
    setIsDeleting(true);
    
    try {
      const response = await api.delete(`/projects/templates/${templateToDelete.id || templateToDelete._id}`);
      
      if (response.data.success) {
        // Remove the deleted template from the state
        setTemplates(prev => prev.filter(t => (t.id || t._id) !== (templateToDelete.id || templateToDelete._id)));
        
        toast.success('Template deleted successfully');
        setShowDeleteModal(false);
        setTemplateToDelete(null);
      } else {
        throw new Error(response.data.error || 'Failed to delete template');
      }
    } catch (error) {
      console.error('Error deleting template:', error);
      toast.error(error.response?.data?.error || error.message || 'Failed to delete template');
    } finally {
      setIsDeleting(false);
    }
  };
  
  // Open delete confirmation modal
  const openDeleteModal = (template) => {
    setTemplateToDelete(template);
    setShowDeleteModal(true);
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-2xl font-semibold text-gray-900">Projects</h1>
          <p className="mt-2 text-sm text-gray-700">
            Manage your projects and project templates
          </p>
        </div>
        <div className="mt-4 sm:mt-0 sm:ml-16 sm:flex-none">
          <Link
            to="/projects/add"
            className="inline-flex items-center justify-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:w-auto"
          >
            <CubeIcon className="-ml-1 mr-2 h-5 w-5" />
            Add Project
          </Link>
          <Link
            to="/projects/templates/new"
            className="ml-3 inline-flex items-center justify-center rounded-md border border-transparent bg-white px-4 py-2 text-sm font-medium text-indigo-600 shadow-sm ring-1 ring-inset ring-indigo-600 hover:bg-indigo-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:w-auto"
          >
            <DocumentDuplicateIcon className="-ml-1 mr-2 h-5 w-5" />
            Add Template
          </Link>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
        </div>
      ) : error ? (
        <div className="rounded-md bg-red-50 p-4 mt-6">
          <div className="flex">
            <div className="flex-shrink-0">
              <ExclamationCircleIcon className="h-5 w-5 text-red-400" aria-hidden="true" />
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">{error}</h3>
            </div>
          </div>
        </div>
      ) : (
        <div>
          <div className="flex justify-between items-center">
            <h1 className="text-2xl font-semibold text-gray-900">Projects</h1>
          </div>
          
          {/* Tabs */}
          <div className="mt-4 border-b border-gray-200">
            <nav className="-mb-px flex space-x-8" aria-label="Tabs">
              <button
                onClick={() => setActiveTab('projects')}
                className={`${
                  activeTab === 'projects'
                    ? 'border-indigo-500 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
              >
                Projects
              </button>
              <button
                onClick={() => setActiveTab('templates')}
                className={`${
                  activeTab === 'templates'
                    ? 'border-indigo-500 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
              >
                Templates
              </button>
            </nav>
          </div>
          
          {activeTab === 'projects' && (
            <div className="mt-8 flex flex-col">
              {/* Search Bar */}
              <div className="mb-4">
                <div className="relative rounded-md shadow-sm max-w-lg">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </div>
                  <input
                    type="text"
                    className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md py-2"
                    placeholder="Search by client or project name..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  {searchTerm && (
                    <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                      <button
                        type="button"
                        onClick={() => setSearchTerm('')}
                        className="text-gray-400 hover:text-gray-500 focus:outline-none"
                      >
                        <XMarkIcon className="h-5 w-5" aria-hidden="true" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Department Filter */}
              <div className="mb-4 flex items-center">
                <span className="mr-3 text-sm font-medium text-gray-700 flex items-center">
                  <FunnelIcon className="h-4 w-4 mr-1" />
                  Filter by Department:
                </span>
                <select
                  value={selectedDepartment}
                  onChange={(e) => setSelectedDepartment(e.target.value)}
                  className="rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                >
                  <option value="all">All Departments</option>
                  {departments.map((department) => (
                    <option key={department.id} value={department.id}>
                      {department.name}
                    </option>
                  ))}
                </select>
                
                <span className="ml-6 mr-3 text-sm font-medium text-gray-700 flex items-center">
                  <FunnelIcon className="h-4 w-4 mr-1" />
                  Filter by Group:
                </span>
                <select
                  value={selectedGroup}
                  onChange={(e) => setSelectedGroup(e.target.value)}
                  className="rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                >
                  <option value="all">All Groups</option>
                  {groups.map((group) => (
                    <option key={group.id} value={group.id}>
                      {group.name}
                    </option>
                  ))}
                </select>
                
                <span className="ml-6 mr-3 text-sm font-medium text-gray-700 flex items-center">
                  <FunnelIcon className="h-4 w-4 mr-1" />
                  Filter by Type:
                </span>
                <select
                  value={selectedType}
                  onChange={(e) => setSelectedType(e.target.value)}
                  className="rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                >
                  <option value="all">All Types</option>
                  {types.map((type) => (
                    <option key={type.id} value={type.id}>
                      {type.name}
                    </option>
                  ))}
                </select>
              </div>
              
              {/* Status Filter */}
              <div className="mb-4 flex items-center">
                <span className="mr-3 text-sm font-medium text-gray-700 flex items-center">
                  <FunnelIcon className="h-4 w-4 mr-1" />
                  Filter by Status:
                </span>
                <select
                  value={selectedStatus}
                  onChange={(e) => setSelectedStatus(e.target.value)}
                  className="rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                >
                  <option value="all">All Statuses</option>
                  {availableStatuses.map((status) => (
                    <option key={status.id} value={status.name}>
                      {status.name}
                    </option>
                  ))}
                </select>
              </div>
              
              {/* Due Date Status Filter */}
              <div className="mb-4 flex items-center">
                <span className="mr-3 text-sm font-medium text-gray-700 flex items-center">
                  <FunnelIcon className="h-4 w-4 mr-1" />
                  Filter by Due Date Status:
                </span>
                <select
                  value={selectedDueStatus}
                  onChange={(e) => setSelectedDueStatus(e.target.value)}
                  className="rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                >
                  <option value="all">All Due Dates</option>
                  <option value="overdue" style={{ backgroundColor: '#FEE2E2', color: '#991B1B', fontWeight: 500 }}>
                    ⚠️ Overdue
                  </option>
                  <option value="warning" style={{ backgroundColor: '#FFEDD5', color: '#9A3412', fontWeight: 500 }}>
                    ⚠ Due within 7 days
                  </option>
                  <option value="normal" style={{ backgroundColor: '#DCFCE7', color: '#166534', fontWeight: 500 }}>
                    ✓ Due later
                  </option>
                  <option value="none" style={{ backgroundColor: '#F3F4F6', color: '#6B7280', fontWeight: 500 }}>
                    Not set
                  </option>
                </select>
              </div>
              
              {/* Display search results count if search is active */}
              {searchTerm && (
                <div className="mb-4 text-sm text-gray-700">
                  <span className="font-medium">{filteredProjects.length}</span> {filteredProjects.length === 1 ? 'result' : 'results'} found for "{searchTerm}"
                </div>
              )}
              
              {/* Due Date Status Legend */}
              <div className="mb-6">
                <h3 className="text-sm font-medium text-gray-700 mb-2">Due Date Status:</h3>
                <div className="flex space-x-4">
                  <div className="flex items-center">
                    <span className="inline-flex rounded-md px-2 py-1 text-xs font-semibold bg-red-100 text-red-800 border border-red-200 mr-2">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5 mr-1 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      Date
                    </span>
                    <span className="text-sm text-gray-600">Overdue</span>
                  </div>
                  <div className="flex items-center">
                    <span className="inline-flex rounded-md px-2 py-1 text-xs font-semibold bg-orange-100 text-orange-800 border border-orange-200 mr-2">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5 mr-1 text-orange-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                      Date
                    </span>
                    <span className="text-sm text-gray-600">Due within 7 days</span>
                  </div>
                  <div className="flex items-center">
                    <span className="inline-flex rounded-md px-2 py-1 text-xs font-semibold bg-green-100 text-green-800 border border-green-200 mr-2">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5 mr-1 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      Date
                    </span>
                    <span className="text-sm text-gray-600">Due later</span>
                  </div>
                </div>
              </div>
              
              <div className="flex items-center space-x-2 mb-6">
                <span className="text-sm font-medium text-gray-700">Show only:</span>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="showCompleted"
                    checked={showCompletedProjects}
                    onChange={() => {
                      setShowCompletedProjects(!showCompletedProjects);
                      // If turning on completed, turn off canceled
                      if (!showCompletedProjects) {
                        setShowCanceledProjects(false);
                      }
                    }}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="showCompleted" className="ml-2 text-sm text-gray-600">
                    Completed projects
                  </label>
                </div>
                <div className="flex items-center ml-4">
                  <input
                    type="checkbox"
                    id="showCanceled"
                    checked={showCanceledProjects}
                    onChange={() => {
                      setShowCanceledProjects(!showCanceledProjects);
                      // If turning on canceled, turn off completed
                      if (!showCanceledProjects) {
                        setShowCompletedProjects(false);
                      }
                    }}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="showCanceled" className="ml-2 text-sm text-gray-600">
                    Canceled projects
                  </label>
                </div>
              </div>
              
              <div className="-my-2 -mx-4 overflow-x-auto sm:-mx-6 lg:-mx-8">
                <div className="inline-block min-w-full py-2 align-middle md:px-6 lg:px-8">
                  <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
                    <table className="min-w-full divide-y divide-gray-300">
                      <thead className="bg-gray-50">
                        <tr>
                          <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">
                            Project Name
                          </th>
                          <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                            Client
                          </th>
                          <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                            Department
                          </th>
                          <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                            Group
                          </th>
                          <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                            Type
                          </th>
                          <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                            Status
                          </th>
                          <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                            Due Date
                          </th>
                          <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                            Start Date
                          </th>
                          <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                            Progress
                          </th>
                          <th scope="col" className="relative py-3.5 pl-3 pr-4 sm:pr-6">
                            <span className="sr-only">View</span>
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200 bg-white">
                        {filteredProjects.length === 0 ? (
                          <tr>
                            <td colSpan="7" className="px-3 py-4 text-sm text-gray-500 text-center">
                              No projects found. Create your first project!
                            </td>
                          </tr>
                        ) : (
                          filteredProjects.map((project) => (
                            <tr key={project._id || project.id}>
                              <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6">
                                <Link to={`/projects/${project._id || project.id}`} className="text-indigo-600 hover:text-indigo-900">
                                  {project.name || 'Unnamed Project'}
                                </Link>
                              </td>
                              <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                {project.client_name ? (
                                  <Link to={`/clients/${project.client_id}`} className="text-indigo-600 hover:text-indigo-900">
                                    {project.client_name}
                                  </Link>
                                ) : project.client_name ? (
                                  <Link to={`/clients/${project.client_id}`} className="text-indigo-600 hover:text-indigo-900">
                                    {project.client_name || 'Unknown Company'}
                                  </Link>
                                ) : project.client ? (
                                  <Link to={`/clients/${project.client._id || project.client.id || project.client_id}`} className="text-indigo-600 hover:text-indigo-900">
                                    {project.client.name || 'Unknown Company'}
                                  </Link>
                                ) : project.client_id ? (
                                  <span>Client #{project.client_id}</span>
                                ) : (
                                  'N/A'
                                )}
                              </td>
                              <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                {project.department_name ? (
                                  <span 
                                    className="inline-flex rounded-full px-2 text-xs font-semibold leading-5 items-center justify-center"
                                    style={{
                                      backgroundColor: project.department_color || '#6B7280',
                                      color: 'white',
                                      minWidth: '80px',
                                      textAlign: 'center'
                                    }}
                                  >
                                    {project.department_name}
                                  </span>
                                ) : (
                                  'Default'
                                )}
                              </td>
                              <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                {project.group_name ? (
                                  <span 
                                    className="inline-flex rounded-full px-2 text-xs font-semibold leading-5 items-center justify-center"
                                    style={{
                                      backgroundColor: project.group_color || '#6B7280',
                                      color: 'white',
                                      minWidth: '80px',
                                      textAlign: 'center'
                                    }}
                                  >
                                    {project.group_name}
                                  </span>
                                ) : (
                                  'Default'
                                )}
                              </td>
                              <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                {project.type_name ? (
                                  <span 
                                    className="inline-flex rounded-full px-2 text-xs font-semibold leading-5 items-center justify-center"
                                    style={{
                                      backgroundColor: project.type_color || '#6B7280',
                                      color: 'white',
                                      minWidth: '80px',
                                      textAlign: 'center'
                                    }}
                                  >
                                    {project.type_name}
                                  </span>
                                ) : (
                                  'Default'
                                )}
                              </td>
                              <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                {getStatusDisplay(project.status)}
                              </td>
                              <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                {project.due_date ? (
                                  <span 
                                    className={`inline-flex rounded-md px-2 py-1 text-xs font-semibold leading-5 items-center justify-center ${
                                      getDueDateStatus(project.due_date) === 'overdue' 
                                        ? 'bg-red-100 text-red-800 border border-red-200' 
                                        : getDueDateStatus(project.due_date) === 'warning'
                                          ? 'bg-orange-100 text-orange-800 border border-orange-200'
                                          : 'bg-green-100 text-green-800 border border-green-200'
                                    }`}
                                  >
                                    {getDueDateStatus(project.due_date) === 'overdue' && (
                                      <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5 mr-1 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                      </svg>
                                    )}
                                    {getDueDateStatus(project.due_date) === 'warning' && (
                                      <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5 mr-1 text-orange-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                      </svg>
                                    )}
                                    {getDueDateStatus(project.due_date) === 'normal' && (
                                      <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5 mr-1 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                      </svg>
                                    )}
                                    {formatDate(project.due_date)}
                                  </span>
                                ) : (
                                  'Not set'
                                )}
                              </td>
                              <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                <span className="inline-flex rounded-md px-2 py-1 text-xs font-semibold leading-5 items-center justify-center bg-blue-100 text-blue-800 border border-blue-200">
                                  <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5 mr-1 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                  </svg>
                                  {formatDate(project.start_date)}
                                </span>
                              </td>
                              <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                <div className="w-full bg-gray-200 rounded-full h-2.5">
                                  <div 
                                    className="bg-indigo-600 h-2.5 rounded-full" 
                                    style={{ width: `${project.progress || 0}%` }}
                                  ></div>
                                </div>
                                <span className="text-xs mt-1">{project.progress || 0}%</span>
                              </td>
                              <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                                {/* View link removed */}
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {activeTab === 'templates' && (
            <div className="mt-8 flex flex-col">
              <div className="-my-2 -mx-4 overflow-x-auto sm:-mx-6 lg:-mx-8">
                <div className="inline-block min-w-full py-2 align-middle md:px-6 lg:px-8">
                  <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
                    <table className="min-w-full divide-y divide-gray-300">
                      <thead className="bg-gray-50">
                        <tr>
                          <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">
                            Template Name
                          </th>
                          <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                            Description
                          </th>
                          <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                            Phases
                          </th>
                          <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                            Last Updated
                          </th>
                          <th scope="col" className="relative py-3.5 pl-3 pr-4 sm:pr-6">
                            <span className="sr-only">Actions</span>
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {/* Show message if no templates */}
                        {templates.length === 0 ? (
                          <tr>
                            <td colSpan="4" className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-center">
                              No templates found
                            </td>
                          </tr>
                        ) : (
                          templates.map((template) => (
                            <tr key={template._id || template.id}>
                              <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6">
                                {template.name || 'Unnamed Template'}
                              </td>
                              <td className="px-3 py-4 text-sm text-gray-500">
                                <div className="max-w-xs truncate">{template.description || 'No description'}</div>
                              </td>
                              <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                {template.phase_count || 0} phases
                              </td>
                              <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                {formatDate(template.updatedAt || template.updated_at)}
                              </td>
                              <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                                <div className="flex space-x-4 justify-end">
                                  <Link to={`/projects/templates/${template._id || template.id}`} className="text-indigo-600 hover:text-indigo-900">
                                    Edit
                                  </Link>
                                  <Link to={`/projects/add?template=${template._id || template.id}`} className="text-green-600 hover:text-green-900">
                                    Use
                                  </Link>
                                  <button
                                    onClick={() => openCloneModal(template)}
                                    className="text-blue-600 hover:text-blue-900"
                                  >
                                    Clone
                                  </button>
                                  <button
                                    onClick={() => openDeleteModal(template)}
                                    className="text-red-600 hover:text-red-900"
                                  >
                                    Delete
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Template clone modal */}
      {showCloneModal && (
        <div className="fixed z-50 inset-0 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            {/* Background overlay */}
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>
            
            {/* Modal panel */}
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">
                      Clone Template
                    </h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500 mb-4">
                        Create a new template based on "{templateToClone?.name}". This will copy all phases, steps, and assignments.
                      </p>
                      <div>
                        <label htmlFor="clone-name" className="block text-sm font-medium text-gray-700">
                          Template Name
                        </label>
                        <input
                          type="text"
                          id="clone-name"
                          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                          value={cloneName}
                          onChange={(e) => setCloneName(e.target.value)}
                          placeholder="Enter name for the cloned template"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  className={`w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-indigo-600 text-base font-medium text-white hover:bg-indigo-700 focus:outline-none sm:ml-3 sm:w-auto sm:text-sm ${isCloning ? 'opacity-75 cursor-not-allowed' : ''}`}
                  onClick={handleCloneTemplate}
                  disabled={isCloning}
                >
                  {isCloning ? 'Cloning...' : 'Clone Template'}
                </button>
                <button
                  type="button"
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  onClick={() => {
                    setShowCloneModal(false);
                    setTemplateToClone(null);
                    setCloneName('');
                  }}
                  disabled={isCloning}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Template delete confirmation modal */}
      {showDeleteModal && templateToDelete && (
        <div className="fixed z-50 inset-0 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            {/* Background overlay */}
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>
            
            {/* Modal panel */}
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                    <svg className="h-6 w-6 text-red-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">
                      Delete Template
                    </h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">
                        Are you sure you want to delete the template "{templateToDelete.name}"? This action cannot be undone.
                      </p>
                      <p className="text-sm text-red-500 mt-2 font-medium">
                        All phases, steps, and template assignments will be permanently deleted.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  className={`w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none sm:ml-3 sm:w-auto sm:text-sm ${isDeleting ? 'opacity-75 cursor-not-allowed' : ''}`}
                  onClick={handleDeleteTemplate}
                  disabled={isDeleting}
                >
                  {isDeleting ? 'Deleting...' : 'Delete Template'}
                </button>
                <button
                  type="button"
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  onClick={() => {
                    setShowDeleteModal(false);
                    setTemplateToDelete(null);
                  }}
                  disabled={isDeleting}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectList; 